import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil, debounceTime } from 'rxjs/operators';
import { Router } from '@angular/router';
import { Store, select } from '@ngrx/store';
import { AppStateInterface } from 'src/app/appState.interface';
import {
  getCustomersSelector,
  getDriversOfCustomersSelector,
  getDriversOfCustomersSelectorAllAssets,
  getSelectedAssests,
  getSelectedAssestsAllAssets,
  getTabAssetChooserRefSelector,
  getVehiclesOfCustomersSelector,
  getVehiclesOfCustomersSelectorAllAssets,
  selectVehicleFilters
} from 'src/app/core/store/selectors';
import { SelectedAssets } from 'src/app/core/store/interface/selectedAssets.interface';
import * as PostActions from '../../../core/store/actions';
import { UtilsService } from 'src/app/core/services/utils.service';
import {
  ROUTES_REQUIRING_ALL_ASSETS,
  VEHICLE_FILTER_INCLUDE_CHILDREN,
  VEHICLE_FILTER_ALSO_DISABLED,
  VEHICLE_FILTER_ALSO_DATA_HIDDEN,
  VEHICLE_FILTER_SUBSCRIBED,
  VEHICLE_FILTER_HAVE_COMMUNICATED,
  MAX_ASSETS_LOAD
} from 'src/app/core/config/config.constants';

// Interfaces for tree view (Model -> Vehicles)
interface VehicleModelNode {
  modelName: string;
  modelUid: string;
  vehicles: any[];
  expanded: boolean;
}

// Interfaces for customer tree view (Customer -> Model -> Vehicles)
interface CustomerNode {
  customerName: string;
  customerUid: string;
  models: VehicleModelNode[];
  expanded: boolean;
}

@Component({
  selector: 'app-assets-chooser-ref-20',
  templateUrl: './assets-chooser-ref-20.component.html',
  styleUrls: ['./assets-chooser-ref-20.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AssetsChooserRef20Component implements OnInit, OnDestroy {

  private destroy$ = new Subject<void>();
  private searchVehicles$ = new Subject<string>();
  private searchDrivers$ = new Subject<string>();

  // Search
  searchTextVehicles: string = '';
  searchTextDrivers: string = '';

  // Data sources
  allVehicles: any[] = [];
  allDrivers: any[] = [];
  allCustomers: any[] = [];
  filteredVehicles: any[] = [];
  filteredDrivers: any[] = [];

  // Pagination
  pageSize: number = 100;
  currentVehiclePage: number = 0;
  currentDriverPage: number = 0;

  // Selections
  selectedVehicleCustomers: any[] = [];
  selectedAssetsDrivers: any[] = [];
  selectAllVehicles: boolean = false;
  selectAllDrivers: boolean = false;

  // Performance: Set per lookup O(1) invece di array.some() O(n)
  private selectedVehicleUidsSet: Set<string> = new Set();
  private selectedDriverUidsSet: Set<string> = new Set();

  // Tab config
  enabledTabPanelItems: any[] = [];
  currentIndex: number = 0;

  // Labels
  textSelectAllVehicles: string = '';
  textSelectAllDrivers: string = '';

  // Counts
  totVehiclesFromStore: number = 0;
  totDriversFromStore: number = 0;

  // State
  emptySelection: boolean = true;
  stylesheetString: string = '';
  isLoadingVehicles: boolean = true;
  isLoadingDrivers: boolean = true;
  isBuildingTree: boolean = false;
  isProcessingSelection: boolean = false; // Flag per Select All in corso
  showMaxAssetsLabel: boolean = false; // Flag per mostrare label limite raggiunto (non bloccante)
  private previousVehiclesLength: number = -1; // Per rilevare cambio dati
  private previousDriversLength: number = -1;

  // Include children toggle (visible only on routes in ROUTES_REQUIRING_ALL_ASSETS)
  showIncludeChildrenToggle: boolean = false;
  includeChildren: boolean = VEHICLE_FILTER_INCLUDE_CHILDREN;
  alsoDisabled: boolean = VEHICLE_FILTER_ALSO_DISABLED;
  subscribed: boolean = VEHICLE_FILTER_SUBSCRIBED;
  alsoDataHidden: boolean = VEHICLE_FILTER_ALSO_DATA_HIDDEN;
  haveCommunicated: boolean = VEHICLE_FILTER_HAVE_COMMUNICATED;

  // View mode: 'list' | 'tree' | 'customer-tree'
  vehicleViewMode: 'list' | 'tree' | 'customer-tree' = 'tree'; // Default: tree view
  vehicleTreeData: VehicleModelNode[] = []; // Model -> Vehicles
  allTreeExpanded: boolean = true; // Track if all tree nodes are expanded

  // Customer tree view data (Customer -> Brand -> Model -> Vehicles)
  customerTreeData: CustomerNode[] = [];

  // Flag to prevent circular updates
  private isDispatchingSelection: boolean = false;

  private appStoreList: SelectedAssets = {
    assetType: '',
    selected: [],
    totSelected: 0,
    isApply: false
  };

  constructor(
    public translateService: TranslateService,
    private utilsService: UtilsService,
    private store: Store<AppStateInterface>,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.stylesheetString = this.utilsService.getStylesheetString();
    this.initLabels();
    this.setupSearchDebounce();
    this.setupSubscriptions();

    // Se partiamo in modalità tree, costruiamolo dopo aver caricato i dati
    if (this.vehicleViewMode === 'tree') {
      this.isBuildingTree = true;
    }
  }

  private initLabels(): void {
    this.textSelectAllVehicles = this.translateService.instant('global.selectAll');
    this.textSelectAllDrivers = this.translateService.instant('global.selectAll');
  }

  private setupSearchDebounce(): void {
    // Debounce per ricerca veicoli (500ms)
    this.searchVehicles$.pipe(
      debounceTime(500),
      takeUntil(this.destroy$)
    ).subscribe(searchText => {
      this.performVehicleFilter(searchText);
      this.cdr.markForCheck();
    });

    // Debounce per ricerca driver (500ms)
    this.searchDrivers$.pipe(
      debounceTime(500),
      takeUntil(this.destroy$)
    ).subscribe(searchText => {
      this.performDriverFilter(searchText);
      this.cdr.markForCheck();
    });
  }

  private setupSubscriptions(): void {
    // Reset loading state per mostrare skeleton
    this.isLoadingVehicles = true;
    this.isLoadingDrivers = true;
    if (this.vehicleViewMode === 'tree') {
      this.isBuildingTree = true;
    }
    this.previousVehiclesLength = -1;
    this.previousDriversLength = -1;
    this.cdr.markForCheck();

    // Determina se la route supporta il toggle per includere children
    this.showIncludeChildrenToggle = ROUTES_REQUIRING_ALL_ASSETS.some(route =>
      this.router.url.startsWith(route)
    );

    // Se la route supporta children, usa includeChildren per determinare il selector
    // Altrimenti usa sempre il selector filtrato
    const requiresAllAssets = this.showIncludeChildrenToggle && this.includeChildren;
   // console.log('[assets-chooser] Route:', this.router.url, '| showIncludeChildrenToggle:', this.showIncludeChildrenToggle, '| includeChildren:', this.includeChildren);

    // Tab config subscription
    this.store.pipe(
      select(getTabAssetChooserRefSelector),
      takeUntil(this.destroy$)
    ).subscribe((res) => {
      this.enabledTabPanelItems = res.tabFilterRef.map(item => ({
        ...item,
        text: this.translateService.instant(item.text)
      }));
    });

    // Customers subscription - per ottenere i nomi dei customer
    this.store.pipe(
      select(getCustomersSelector),
      takeUntil(this.destroy$)
    ).subscribe((customers) => {
      this.allCustomers = customers || [];
    });

    // Vehicles subscription - scegli il selector in base alla route
    const vehiclesSelector = requiresAllAssets
      ? getVehiclesOfCustomersSelectorAllAssets
      : getVehiclesOfCustomersSelector;

    this.store.pipe(
      select(vehiclesSelector),
      takeUntil(this.destroy$)
    ).subscribe((vehicles) => {
    //  console.log('[assets-chooser] Vehicles from store:', vehicles.length, '| Sample isChild values:', vehicles.slice(0, 3).map((v: any) => v.isChild));

      // DEBUG: Count per ogni filtro
      const countIsChild = vehicles.filter((v: any) => v.isChild === true).length;
      const countNotChild = vehicles.filter((v: any) => !v.isChild).length;
      const countEnabled = vehicles.filter((v: any) => v.enabled !== false).length;
      const countDisabled = vehicles.filter((v: any) => v.enabled === false).length;
      const countSubscribed = vehicles.filter((v: any) => v.subscribed === true).length;
      const countNotSubscribed = vehicles.filter((v: any) => v.subscribed !== true).length;
      const countDataHidden = vehicles.filter((v: any) => v.dataHidden === true).length;
      const countNotDataHidden = vehicles.filter((v: any) => v.dataHidden !== true).length;
      const countHaveCommunicated = vehicles.filter((v: any) => v.haveCommunicated === true).length;
      const countNotHaveCommunicated = vehicles.filter((v: any) => v.haveCommunicated !== true).length;
      // console.log('[assets-chooser] FILTER COUNTS:', {
      //   total: vehicles.length,
      //   isChild: countIsChild,
      //   notChild: countNotChild,
      //   enabled: countEnabled,
      //   disabled: countDisabled,
      //   subscribed: countSubscribed,
      //   notSubscribed: countNotSubscribed,
      //   dataHidden: countDataHidden,
      //   notDataHidden: countNotDataHidden,
      //   haveCommunicated: countHaveCommunicated,
      //   notHaveCommunicated: countNotHaveCommunicated
      // });

      // Rileva se i dati sono cambiati (nuovo customer o refresh)
      const firstVehicleUid = this.allVehicles.length > 0 ? (this.allVehicles[0] as any)?.uid : null;
      const newFirstVehicleUid = vehicles.length > 0 ? (vehicles[0] as any)?.uid : null;
      const dataChanged = this.previousVehiclesLength !== vehicles.length ||
                          (this.previousVehiclesLength > 0 && vehicles.length > 0 &&
                           firstVehicleUid !== newFirstVehicleUid);

      if (dataChanged && !this.isLoadingVehicles) {
        // Mostra skeleton perché i dati stanno cambiando
        this.isLoadingVehicles = true;
        this.allVehicles = [];
        this.filteredVehicles = [];
        if (this.vehicleViewMode === 'tree' || this.vehicleViewMode === 'customer-tree') {
          this.isBuildingTree = true;
        }
        this.cdr.markForCheck();
      }

      this.previousVehiclesLength = vehicles.length;

      // Mantieni skeleton visibile per almeno 300ms per dare feedback visivo
      setTimeout(() => {
        this.totVehiclesFromStore = vehicles.length;
        this.allVehicles = this.processVehicles(vehicles);

        // Leggi i filtri dallo store e applicali
        this.store.pipe(
          select(selectVehicleFilters),
          takeUntil(this.destroy$)
        ).subscribe(filters => {
          this.includeChildren = filters.includeChildren;
          this.alsoDisabled = filters.alsoDisabled;
          this.alsoDataHidden = filters.alsoDataHidden;
          this.subscribed = filters.subscribed;
          this.haveCommunicated = filters.haveCommunicated;
        });

        // Applica i filtri correnti invece di copiare tutto
        this.performVehicleFilter(this.searchTextVehicles);
        this.currentVehiclePage = 0;
        this.isLoadingVehicles = false;
        this.isBuildingTree = false;

        this.cdr.markForCheck();
      }, 300);
    });

    // Drivers subscription - scegli il selector in base alla route
    const driversSelector = requiresAllAssets
      ? getDriversOfCustomersSelectorAllAssets
      : getDriversOfCustomersSelector;

   // console.log('[assets-chooser] Drivers selector chosen:', requiresAllAssets ? 'getDriversOfCustomersSelectorAllAssets' : 'getDriversOfCustomersSelector');

    // PRIMA verifichiamo lo stato RAW dello store
    this.store.pipe(
      select((state: any) => state.getDriversOfCustomers),
      takeUntil(this.destroy$)
    ).subscribe((rawDriverState) => {
     // console.log('[assets-chooser] RAW DRIVER STATE from store:', rawDriverState);
      //console.log('[assets-chooser] RAW DRIVER STATE posts:', rawDriverState?.posts);
      //console.log('[assets-chooser] RAW DRIVER STATE posts length:', rawDriverState?.posts?.length);
    });

    this.store.pipe(
      select(driversSelector),
      takeUntil(this.destroy$)
    ).subscribe((drivers) => {
      // console.log('[assets-chooser] ===== DRIVERS SUBSCRIPTION =====');
      // console.log('[assets-chooser] Drivers from store:', drivers);
      // console.log('[assets-chooser] Drivers length:', drivers?.length);
      // console.log('[assets-chooser] Drivers type:', typeof drivers, '| isArray:', Array.isArray(drivers));
      // console.log('[assets-chooser] Drivers sample:', drivers?.slice(0, 3));
      // console.log('[assets-chooser] ==============================');

      // Rileva se i dati sono cambiati
      const driversArray = drivers as any[];
      const firstDriverUid = this.allDrivers.length > 0 ? (this.allDrivers[0] as any)?.uid : null;
      const newFirstDriverUid = driversArray.length > 0 ? driversArray[0]?.uid : null;
      const dataChanged = this.previousDriversLength !== driversArray.length ||
                          (this.previousDriversLength > 0 && driversArray.length > 0 &&
                           firstDriverUid !== newFirstDriverUid);

     // console.log('[assets-chooser] Drivers dataChanged:', dataChanged, '| previousLength:', this.previousDriversLength, '| newLength:', driversArray.length);

      if (dataChanged && !this.isLoadingDrivers) {
        // Mostra skeleton perché i dati stanno cambiando
        // console.log('[assets-chooser] Showing drivers skeleton...');
        this.isLoadingDrivers = true;
        this.allDrivers = [];
        this.filteredDrivers = [];
        this.cdr.markForCheck();
      }

      this.previousDriversLength = driversArray.length;

      // Mantieni skeleton visibile per almeno 300ms
      setTimeout(() => {
        this.totDriversFromStore = driversArray.length;
        this.allDrivers = this.processDrivers(driversArray);
        this.filteredDrivers = [...this.allDrivers];
        // console.log('[assets-chooser] Drivers processed | allDrivers:', this.allDrivers.length, '| filteredDrivers:', this.filteredDrivers.length);
        // console.log('[assets-chooser] First 3 drivers:', this.filteredDrivers.slice(0, 3));
        this.currentDriverPage = 0;
        this.isLoadingDrivers = false;
        this.cdr.markForCheck();
      }, 300);
    });

    // Selected assets subscription - scegli il selector in base alla route
    const selectedAssetsSelector = requiresAllAssets
      ? getSelectedAssestsAllAssets
      : getSelectedAssests;

    this.store.pipe(
      select(selectedAssetsSelector),
      takeUntil(this.destroy$)
    ).subscribe((selectedAssets) => {
      //console.log('[assets-chooser] Selected assets from store:', selectedAssets?.assetType, '| count:', selectedAssets?.selected?.length, '| totSelected:', selectedAssets?.totSelected);
      this.handleSelectedAssetsChange(selectedAssets);
      this.cdr.markForCheck();
    });
  }

  private processVehicles(vehicles: any[]): any[] {
    return vehicles.map(vehicle => {
      // Processa assignationHistory per aggiungere customerName
      let processedAssignationHistory = vehicle.assignationHistory;
      if (vehicle.assignationHistory && vehicle.assignationHistory.length > 0) {
        // Trova l'entry con initDate maggiore
        const latestAssignation = vehicle.assignationHistory.reduce((latest: any, current: any) => {
          return (current.initDate > latest.initDate) ? current : latest;
        }, vehicle.assignationHistory[0]);

        // Cerca il customer corrispondente e aggiungi customerName
        const customer = this.allCustomers.find(c => c.uid === latestAssignation.uidCustomer);

        // Mappa assignationHistory aggiungendo customerName all'entry con initDate maggiore
        processedAssignationHistory = vehicle.assignationHistory.map((entry: any) => {
          if (entry.initDate === latestAssignation.initDate && entry.uidCustomer === latestAssignation.uidCustomer) {
            return {
              ...entry,
              customerName: customer?.name || ''
            };
          }
          return entry;
        });
      }

      return {
        ...vehicle,
        assignationHistory: processedAssignationHistory,
        text: `${vehicle.internalId} ( ${vehicle.plate} )`,
        trailerFind: vehicle.vehicleType?.idVehicleCategory === 3
          ? this.translateService.instant('global.vehicleTypes.semitrailer')
          : ''
      };
    }).sort((a, b) => (a.internalId || '').localeCompare(b.internalId || ''));
  }

  private processDrivers(drivers: any[]): any[] {
    return drivers.map(driver => ({
      ...driver,
      text: `${driver.fullName} (${driver.cardId})`
    })).sort((a, b) => (a.fullName || '').localeCompare(b.fullName || ''));
  }

  private handleSelectedAssetsChange(selectedAssets: SelectedAssets): void {
    // Skip if we're currently dispatching to avoid circular updates
    if (this.isDispatchingSelection) {
      return;
    }

    switch (selectedAssets.assetType) {
      case 'VEHIC':
        // Applica il limite MAX_ASSETS_LOAD quando arrivano asset dallo store
        // NON mostriamo il warning perché questo è un caricamento automatico, non una selezione manuale
        if (selectedAssets.selected.length > MAX_ASSETS_LOAD) {
          this.selectedVehicleCustomers = selectedAssets.selected.slice(0, MAX_ASSETS_LOAD);
          // Dispatch della selezione limitata per aggiornare lo store
          setTimeout(() => {
            this.dispatchVehicleSelection();
          }, 0);
        } else {
          this.selectedVehicleCustomers = selectedAssets.selected;
        }
        this.selectedAssetsDrivers = [];
        this.syncVehicleSelectionSet();
        this.syncDriverSelectionSet();
        this.updateSelectAllVehiclesLabel();
        this.textSelectAllDrivers = this.translateService.instant('global.selectAll');
        this.selectAllDrivers = false;
        this.emptySelection = false;
        this.currentIndex = 0;
        break;

      case 'DRIVR':
        // Applica il limite MAX_ASSETS_LOAD anche per i driver
        // NON mostriamo il warning perché questo è un caricamento automatico, non una selezione manuale
        if (selectedAssets.selected.length > MAX_ASSETS_LOAD) {
          this.selectedAssetsDrivers = selectedAssets.selected.slice(0, MAX_ASSETS_LOAD);
          // Dispatch della selezione limitata per aggiornare lo store
          setTimeout(() => {
            this.dispatchDriverSelection();
          }, 0);
        } else {
          this.selectedAssetsDrivers = selectedAssets.selected;
        }
        this.selectedVehicleCustomers = [];
        this.syncDriverSelectionSet();
        this.syncVehicleSelectionSet();
        this.updateSelectAllDriversLabel();
        this.textSelectAllVehicles = this.translateService.instant('global.selectAll');
        this.selectAllVehicles = false;
        this.emptySelection = false;
        this.currentIndex = 1;
        break;
    }
  }

  private updateSelectAllVehiclesLabel(): void {
    // Ottimizzazione: conta quanti veicoli filtrati sono selezionati usando il Set O(n) con lookup O(1)
    let allFilteredSelected = false;
    if (this.filteredVehicles.length > 0) {
      // Se il Set ha meno elementi dei filtrati, sicuramente non sono tutti selezionati
      if (this.selectedVehicleUidsSet.size >= this.filteredVehicles.length) {
        allFilteredSelected = this.filteredVehicles.every(v => this.selectedVehicleUidsSet.has(v.uid));
      }
    }

    // Se siamo al limite MAX_ASSETS_LOAD o tutti i filtrati sono selezionati, mostra "Deselect All"
    if (allFilteredSelected || this.selectedVehicleCustomers.length >= MAX_ASSETS_LOAD) {
      this.textSelectAllVehicles = this.translateService.instant('global.deSelectAll');
      this.selectAllVehicles = true;
      this.showMaxAssetsLabel = this.selectedVehicleCustomers.length >= MAX_ASSETS_LOAD;
    } else {
      this.textSelectAllVehicles = this.translateService.instant('global.selectAll');
      this.selectAllVehicles = false;
      this.showMaxAssetsLabel = false;
    }
  }

  private updateSelectAllDriversLabel(): void {
    // Ottimizzazione: conta quanti driver filtrati sono selezionati usando il Set O(n) con lookup O(1)
    let allFilteredSelected = false;
    if (this.filteredDrivers.length > 0) {
      // Se il Set ha meno elementi dei filtrati, sicuramente non sono tutti selezionati
      if (this.selectedDriverUidsSet.size >= this.filteredDrivers.length) {
        allFilteredSelected = this.filteredDrivers.every(d => this.selectedDriverUidsSet.has(d.uid));
      }
    }

    if (allFilteredSelected) {
      this.textSelectAllDrivers = this.translateService.instant('global.deSelectAll');
      this.selectAllDrivers = true;
    } else {
      this.textSelectAllDrivers = this.translateService.instant('global.selectAll');
      this.selectAllDrivers = false;
    }
  }

  // Pagination getters
  get paginatedVehicles(): any[] {
    const start = this.currentVehiclePage * this.pageSize;
    return this.filteredVehicles.slice(start, start + this.pageSize);
  }

  get paginatedDrivers(): any[] {
    const start = this.currentDriverPage * this.pageSize;
    return this.filteredDrivers.slice(start, start + this.pageSize);
  }

  get totalVehiclePages(): number {
    return Math.ceil(this.filteredVehicles.length / this.pageSize);
  }

  get totalDriverPages(): number {
    return Math.ceil(this.filteredDrivers.length / this.pageSize);
  }

  getVehiclePaginationInfo(): string {
    const start = this.currentVehiclePage * this.pageSize + 1;
    const end = Math.min((this.currentVehiclePage + 1) * this.pageSize, this.filteredVehicles.length);
    return `${start}-${end} / ${this.filteredVehicles.length}`;
  }

  getDriverPaginationInfo(): string {
    const start = this.currentDriverPage * this.pageSize + 1;
    const end = Math.min((this.currentDriverPage + 1) * this.pageSize, this.filteredDrivers.length);
    return `${start}-${end} / ${this.filteredDrivers.length}`;
  }

  goToVehiclePage(page: number): void {
    if (page >= 0 && page < this.totalVehiclePages) {
      this.currentVehiclePage = page;
    }
  }

  goToDriverPage(page: number): void {
    if (page >= 0 && page < this.totalDriverPages) {
      this.currentDriverPage = page;
    }
  }

  // Selection helpers - O(1) lookup usando Set
  isVehicleSelected(vehicle: any): boolean {
    return this.selectedVehicleUidsSet.has(vehicle.uid);
  }

  isDriverSelected(driver: any): boolean {
    return this.selectedDriverUidsSet.has(driver.uid);
  }

  // Metodo per sincronizzare il Set quando cambia l'array di selezione
  private syncVehicleSelectionSet(): void {
    this.selectedVehicleUidsSet = new Set(this.selectedVehicleCustomers.map(v => v.uid));
  }

  private syncDriverSelectionSet(): void {
    this.selectedDriverUidsSet = new Set(this.selectedAssetsDrivers.map(d => d.uid));
  }

  toggleVehicleSelection(vehicle: any, selectionMode: string): void {
    if (selectionMode === 'all') {
      // Multiple selection - usa il Set per check O(1)
      if (this.selectedVehicleUidsSet.has(vehicle.uid)) {
        this.selectedVehicleCustomers = this.selectedVehicleCustomers.filter(v => v.uid !== vehicle.uid);
        this.selectedVehicleUidsSet.delete(vehicle.uid);
      } else {
        // Controlla il limite MAX_ASSETS_LOAD prima di aggiungere
        if (this.selectedVehicleCustomers.length >= MAX_ASSETS_LOAD) {
          this.showMaxAssetsWarning();
          return;
        }
        this.selectedVehicleCustomers = [...this.selectedVehicleCustomers, vehicle];
        this.selectedVehicleUidsSet.add(vehicle.uid);
      }
    } else {
      // Single selection
      this.selectedVehicleCustomers = [vehicle];
      this.syncVehicleSelectionSet();
    }

    this.updateSelectAllVehiclesLabel();
    this.onSelectionChangedVehicleCustomer();
  }

  toggleDriverSelection(driver: any, selectionMode: string): void {
    if (selectionMode === 'all') {
      // Multiple selection - usa il Set per check O(1)
      if (this.selectedDriverUidsSet.has(driver.uid)) {
        this.selectedAssetsDrivers = this.selectedAssetsDrivers.filter(d => d.uid !== driver.uid);
        this.selectedDriverUidsSet.delete(driver.uid);
      } else {
        this.selectedAssetsDrivers = [...this.selectedAssetsDrivers, driver];
        this.selectedDriverUidsSet.add(driver.uid);
      }
    } else {
      // Single selection
      this.selectedAssetsDrivers = [driver];
      this.syncDriverSelectionSet();
    }

    this.updateSelectAllDriversLabel();
    this.onSelectionChangedDrivers();
  }

  onTabChanged(event: any): void {
    this.currentIndex = event.index;
  }

  filterVehicles(): void {
    this.searchVehicles$.next(this.searchTextVehicles);
  }

  private performVehicleFilter(searchText: string): void {
  //  console.log('[performVehicleFilter] START | allVehicles:', this.allVehicles.length, '| searchText:', searchText);
    // console.log('[performVehicleFilter] Current filter values:', {
    //   includeChildren: this.includeChildren,
    //   alsoDisabled: this.alsoDisabled,
    //   subscribed: this.subscribed,
    //   alsoDataHidden: this.alsoDataHidden,
    //   haveCommunicated: this.haveCommunicated
    // });

    const search = searchText.toLowerCase().trim();
    let filtered = [...this.allVehicles];
  //  console.log('[performVehicleFilter] After copy:', filtered.length);

    // Apply search filter - i campi cercati dipendono dalla vista corrente
    if (search) {
      filtered = filtered.filter(v => this.matchesVehicleSearch(v, search));
    }

    // Apply toolbar filters
    if (!this.includeChildren) {
      filtered = filtered.filter(v => !v.isChild);
      // console.log('[performVehicleFilter] After includeChildren filter:', filtered.length);
    }

    if (!this.alsoDisabled) {
      filtered = filtered.filter(v => v.enabled !== false);
      // console.log('[performVehicleFilter] After alsoDisabled filter:', filtered.length);
    }

    if (!this.alsoDataHidden) {
      filtered = filtered.filter(v => v.dataHidden !== true);
      // console.log('[performVehicleFilter] After alsoDataHidden filter:', filtered.length);
    }

    // subscribed: se FALSE mostra SOLO i NON subscribed, se TRUE mostra TUTTI (aggiunge i subscribed)
    if (!this.subscribed) {
      filtered = filtered.filter(v => v.subscribed !== true);
      // console.log('[performVehicleFilter] After subscribed filter (only non-subscribed):', filtered.length);
    }

    // haveCommunicated: se FALSE mostra TUTTI, se TRUE mostra SOLO chi ha comunicato (haveCommunicated = true)
    if (this.haveCommunicated) {
      filtered = filtered.filter(v => v.haveCommunicated === true);
      // console.log('[performVehicleFilter] After haveCommunicated filter (only communicated):', filtered.length);
    }

    // console.log('[performVehicleFilter] FINAL:', filtered.length);
    // console.log('[performVehicleFilter] ===== RIEPILOGO FILTRI APPLICATI =====');
    // console.log('[performVehicleFilter] Totale veicoli iniziali:', this.allVehicles.length);
    // console.log('[performVehicleFilter] Filtri attivi:', {
    //   searchText: this.searchTextVehicles || '(nessuna ricerca)',
    //   includeChildren: this.includeChildren,
    //   alsoDisabled: this.alsoDisabled,
    //   subscribed: this.subscribed,
    //   alsoDataHidden: this.alsoDataHidden,
    //   haveCommunicated: this.haveCommunicated
    // });
    // console.log('[performVehicleFilter] Totale veicoli dopo filtri:', filtered.length);
    // console.log('[performVehicleFilter] =====================================');

    // Sort filtered vehicles by internalId to maintain consistent order in list view
    this.filteredVehicles = filtered.sort((a, b) => (a.internalId || '').localeCompare(b.internalId || ''));
    this.currentVehiclePage = 0;
    this.updateSelectAllVehiclesLabel();
    // Rebuild tree if in tree view mode
    if (this.vehicleViewMode === 'tree') {
      this.buildVehicleTree();
    } else if (this.vehicleViewMode === 'customer-tree') {
      this.buildCustomerVehicleTree();
    }
  }

  /**
   * Match della ricerca veicoli in base alla vista:
   * - list: VIN / internalId / targa
   * - tree (Brand -> Model): brand e model
   * - customer-tree (Customer -> Brand -> Model): customer, brand e model
   */
  private matchesVehicleSearch(v: any, search: string): boolean {
    const has = (value: any): boolean =>
      typeof value === 'string' && value.toLowerCase().includes(search);

    const brandName = v.vehicleModel?.vehicleBrand?.name;
    const modelName = v.vehicleModel?.name;

    switch (this.vehicleViewMode) {
      case 'tree':
        return has(brandName) || has(modelName);
      case 'customer-tree':
        return has(this.getVehicleCustomerName(v)) || has(brandName) || has(modelName);
      case 'list':
      default:
        return has(v.vin) || has(v.internalId) || has(v.plate);
    }
  }

  filterDrivers(): void {
    this.searchDrivers$.next(this.searchTextDrivers);
  }

  private performDriverFilter(searchText: string): void {
    const search = searchText.toLowerCase().trim();
    if (!search) {
      this.filteredDrivers = [...this.allDrivers];
    } else {
      this.filteredDrivers = this.allDrivers.filter(d =>
        (d.internalId?.toLowerCase().includes(search)) ||
        (d.fullName?.toLowerCase().includes(search)) ||
        (d.name?.toLowerCase().includes(search)) ||
        (d.cardId?.toLowerCase().includes(search))
      );
    }
    this.currentDriverPage = 0;
    this.updateSelectAllDriversLabel();
  }

  onSelectAllVehiclesChange(event: any): void {
    const LARGE_DATASET_THRESHOLD = 500; // Soglia per considerare dataset "grande"

    // OTTIMIZZAZIONE CHIAVE: Collassa il tree PRIMA di processare per ridurre DOM updates
    const isLargeDataset = this.filteredVehicles.length > LARGE_DATASET_THRESHOLD;
    if (isLargeDataset && (this.vehicleViewMode === 'tree' || this.vehicleViewMode === 'customer-tree')) {
      this.collapseAllTreeNodes();
      this.cdr.detectChanges(); // Forza update DOM subito
    }

    if (event.checked) {
      // SELECT ALL - Controlla il limite MAX_ASSETS_LOAD
      const newVehicles = this.filteredVehicles.filter(v => !this.selectedVehicleUidsSet.has(v.uid));
      const totalAfterSelection = this.selectedVehicleCustomers.length + newVehicles.length;

      // Se supera il limite, prendi solo fino a MAX_ASSETS_LOAD e avvisa
      if (totalAfterSelection > MAX_ASSETS_LOAD) {
        const availableSlots = MAX_ASSETS_LOAD - this.selectedVehicleCustomers.length;
        if (availableSlots <= 0) {
          this.showMaxAssetsWarning();
          return;
        }

        // Seleziona solo quelli che ci stanno
        const vehiclesToAdd = newVehicles.slice(0, availableSlots);
        this.selectedVehicleCustomers = [...this.selectedVehicleCustomers, ...vehiclesToAdd];
        vehiclesToAdd.forEach(v => this.selectedVehicleUidsSet.add(v.uid));
        this.showMaxAssetsWarning();
      } else {
        // Selezione normale senza superare il limite
        this.selectedVehicleCustomers = [...this.selectedVehicleCustomers, ...newVehicles];
        newVehicles.forEach(v => this.selectedVehicleUidsSet.add(v.uid));
        this.textSelectAllVehicles = this.translateService.instant('global.deSelectAll');
        this.selectAllVehicles = true;
      }

      // Dispatch in modo asincrono per non bloccare UI
      requestAnimationFrame(() => {
        this.dispatchVehicleSelection();
        this.isProcessingSelection = false;
        this.cdr.markForCheck();
      });
    } else {
      // DESELECT ALL - Rimuovi tutti i veicoli filtrati
      const filteredUids = new Set(this.filteredVehicles.map(v => v.uid));

      // Aggiorna tutto in memoria prima (veloce)
      this.selectedVehicleCustomers = this.selectedVehicleCustomers.filter(v => !filteredUids.has(v.uid));
      filteredUids.forEach(uid => this.selectedVehicleUidsSet.delete(uid));
      this.textSelectAllVehicles = this.translateService.instant('global.selectAll');
      this.selectAllVehicles = false;
      // Reset cap label dopo deselect: ricalcola in base alla selezione residua
      this.showMaxAssetsLabel = this.selectedVehicleCustomers.length >= MAX_ASSETS_LOAD;

      // Dispatch in modo asincrono per non bloccare UI
      requestAnimationFrame(() => {
        this.dispatchVehicleSelection();
        this.isProcessingSelection = false;
        this.cdr.markForCheck();
      });
    }
  }

  /**
   * Collassa tutti i nodi del tree per ridurre elementi DOM durante operazioni pesanti
   */
  private collapseAllTreeNodes(): void {
    this.allTreeExpanded = false;

    if (this.vehicleViewMode === 'tree') {
      this.vehicleTreeData.forEach(model => {
        model.expanded = false;
      });
    } else if (this.vehicleViewMode === 'customer-tree') {
      this.customerTreeData.forEach(customer => {
        customer.expanded = false;
        customer.models.forEach(model => {
          model.expanded = false;
        });
      });
    }
  }

  onSelectAllDriversChange(event: any): void {
    if (event.checked) {
      // Add all filtered drivers to selection (merge with existing) - usa Set per O(1) lookup
      const newDrivers = this.filteredDrivers.filter(d => !this.selectedDriverUidsSet.has(d.uid));
      this.selectedAssetsDrivers = [...this.selectedAssetsDrivers, ...newDrivers];
      // Aggiorna il Set in bulk
      newDrivers.forEach(d => this.selectedDriverUidsSet.add(d.uid));
      this.textSelectAllDrivers = this.translateService.instant('global.deSelectAll');
    } else {
      // Remove all filtered drivers from selection
      const filteredUids = new Set(this.filteredDrivers.map(d => d.uid));
      this.selectedAssetsDrivers = this.selectedAssetsDrivers.filter(d => !filteredUids.has(d.uid));
      // Aggiorna il Set rimuovendo gli uid filtrati
      filteredUids.forEach(uid => this.selectedDriverUidsSet.delete(uid));
      this.textSelectAllDrivers = this.translateService.instant('global.selectAll');
    }
    this.dispatchDriverSelection();
  }

  onSelectionChangedVehicleCustomer(): void {
    if (this.selectedVehicleCustomers.length > 0) {
      this.dispatchVehicleSelection();
      this.selectedAssetsDrivers = [];
      this.selectedDriverUidsSet.clear();
      this.selectAllDrivers = false;
      this.emptySelection = false;
    } else {
      this.checkEmptySelection();
    }
  }

  onSelectionChangedDrivers(): void {
    if (this.selectedAssetsDrivers.length > 0) {
      this.dispatchDriverSelection();
      this.selectedVehicleCustomers = [];
      this.selectedVehicleUidsSet.clear();
      this.selectAllVehicles = false;
      this.emptySelection = false;
    } else {
      this.checkEmptySelection();
    }
  }

  private dispatchVehicleSelection(): void {
    this.isDispatchingSelection = true;
    this.appStoreList = {
      assetType: 'VEHIC',
      selected: this.selectedVehicleCustomers,
      totSelected: this.selectedVehicleCustomers.length,
      isApply: false,
      isCapped: this.selectedVehicleCustomers.length >= MAX_ASSETS_LOAD
    };
     //@FZ tolto dispatch momentaneo per testare
    this.store.dispatch(PostActions.setSelectedAssets(this.appStoreList));
    // Reset flag after a tick to allow future store updates
    setTimeout(() => this.isDispatchingSelection = false, 0);
  }

  private dispatchDriverSelection(): void {
    this.isDispatchingSelection = true;
    this.appStoreList = {
      assetType: 'DRIVR',
      selected: this.selectedAssetsDrivers,
      totSelected: this.selectedAssetsDrivers.length,
      isApply: false
    };
     //@FZ tolto dispatch momentaneo per testare
    //this.store.dispatch(PostActions.setSelectedAssets(this.appStoreList));
    // Reset flag after a tick to allow future store updates
    setTimeout(() => this.isDispatchingSelection = false, 0);
  }

  private checkEmptySelection(): void {
    if (this.selectedVehicleCustomers.length <= 0 && this.selectedAssetsDrivers.length <= 0) {
      this.emptySelection = true;
    }
  }

  onIncludeChildrenChange(event: any): void {
    this.includeChildren = event.checked;
   // console.log('[assets-chooser] Include children changed to:', this.includeChildren);

    // Ricarica le subscriptions con il nuovo valore
    this.destroy$.next(); // Termina le subscriptions esistenti
    this.setupSubscriptions(); // Ricrea le subscriptions con il nuovo selector
  }

  // Set vehicle view mode
  setVehicleViewMode(mode: 'list' | 'tree' | 'customer-tree'): void {
    if (this.vehicleViewMode === mode) return;

    const LARGE_DATASET_THRESHOLD = 500;
    const isLargeDataset = this.filteredVehicles.length > LARGE_DATASET_THRESHOLD;
    const wasTreeView = this.vehicleViewMode === 'tree' || this.vehicleViewMode === 'customer-tree';

    // Cambia immediatamente la modalità per evitare doppi click
    this.vehicleViewMode = mode;
    this.cdr.markForCheck();

    // OTTIMIZZAZIONE: Collassa il tree corrente prima di cambiare view per ridurre DOM updates
    if (isLargeDataset && wasTreeView && mode === 'list') {
      // Usa setTimeout per rendere asincrona l'operazione di collasso
      setTimeout(() => {
        this.collapseAllTreeNodes();
        this.cdr.markForCheck();
      }, 0);
    } else if (isLargeDataset && wasTreeView) {
      this.collapseAllTreeNodes();
      this.cdr.detectChanges();
    }

    if (mode === 'tree' || mode === 'customer-tree') {
      this.isBuildingTree = true;
      this.cdr.markForCheck();
      setTimeout(() => {
        // performVehicleFilter riapplica la ricerca con i campi della nuova vista e ricostruisce il tree
        this.performVehicleFilter(this.searchTextVehicles);
        this.isBuildingTree = false;
        this.cdr.markForCheck();
      }, 400);
    } else {
      // Vista lista: riapplica la ricerca sui campi VIN / internalId / targa
      this.performVehicleFilter(this.searchTextVehicles);
      this.cdr.markForCheck();
    }
  }

  // Toggle expand/collapse all tree nodes
  toggleExpandAll(): void {
    this.allTreeExpanded = !this.allTreeExpanded;

    if (this.vehicleViewMode === 'tree') {
      this.vehicleTreeData.forEach(model => {
        model.expanded = this.allTreeExpanded;
      });
    } else if (this.vehicleViewMode === 'customer-tree') {
      this.customerTreeData.forEach(customer => {
        customer.expanded = this.allTreeExpanded;
        customer.models.forEach(model => {
          model.expanded = this.allTreeExpanded;
        });
      });
    }

    this.cdr.markForCheck();
  }

  // Build tree structure: Model → Vehicles
  private buildVehicleTree(): void {
    const modelMap = new Map<string, VehicleModelNode>();
    const LARGE_DATASET_THRESHOLD = 500;
    const shouldStartCollapsed = this.filteredVehicles.length > LARGE_DATASET_THRESHOLD;

    // Se dataset grande, i nodi partono collassati
    if (shouldStartCollapsed) {
      this.allTreeExpanded = false;
    }

    this.filteredVehicles.forEach(vehicle => {
      const modelUid = vehicle.vehicleModel?.uid || 'unknown';
      const modelName = vehicle.vehicleModel?.name || 'Unknown Model';

      if (!modelMap.has(modelUid)) {
        modelMap.set(modelUid, {
          modelUid,
          modelName,
          vehicles: [],
          expanded: !shouldStartCollapsed
        });
      }

      const modelNode = modelMap.get(modelUid)!;
      modelNode.vehicles.push(vehicle);
    });

    // Sort models and vehicles
    this.vehicleTreeData = Array.from(modelMap.values())
      .sort((a, b) => a.modelName.localeCompare(b.modelName))
      .map(model => ({
        ...model,
        vehicles: model.vehicles.sort((a, b) => (a.internalId || '').localeCompare(b.internalId || ''))
      }));
  }

  // Build tree structure: Customer → Model → Vehicles
  private buildCustomerVehicleTree(): void {
    const customerMap = new Map<string, CustomerNode>();
    const LARGE_DATASET_THRESHOLD = 500;
    const shouldStartCollapsed = this.filteredVehicles.length > LARGE_DATASET_THRESHOLD;

    // Se dataset grande, i nodi partono collassati
    if (shouldStartCollapsed) {
      this.allTreeExpanded = false;
    }

    this.filteredVehicles.forEach(vehicle => {
      // Ottieni il customerName dall'assignationHistory (entry con initDate maggiore)
      const customerName = this.getVehicleCustomerName(vehicle) || 'Unknown Customer';
      // Usa uidCustomer dall'entry più recente
      let customerUid = 'unknown';
      if (vehicle.assignationHistory && vehicle.assignationHistory.length > 0) {
        const latestAssignation = vehicle.assignationHistory.reduce((latest: any, current: any) => {
          return (current.initDate > latest.initDate) ? current : latest;
        }, vehicle.assignationHistory[0]);
        customerUid = latestAssignation.uidCustomer || 'unknown';
      }

      const modelUid = vehicle.vehicleModel?.uid || 'unknown';
      const modelName = vehicle.vehicleModel?.name || 'Unknown Model';

      // Get or create customer node
      if (!customerMap.has(customerUid)) {
        customerMap.set(customerUid, {
          customerUid,
          customerName,
          models: [],
          expanded: !shouldStartCollapsed
        });
      }

      const customerNode = customerMap.get(customerUid)!;

      // Get or create model node within customer
      let modelNode = customerNode.models.find(m => m.modelUid === modelUid);
      if (!modelNode) {
        modelNode = {
          modelUid,
          modelName,
          vehicles: [],
          expanded: !shouldStartCollapsed
        };
        customerNode.models.push(modelNode);
      }

      modelNode.vehicles.push(vehicle);
    });

    // Sort customers, models, and vehicles
    this.customerTreeData = Array.from(customerMap.values())
      .sort((a, b) => a.customerName.localeCompare(b.customerName))
      .map(customer => ({
        ...customer,
        models: customer.models
          .sort((a, b) => a.modelName.localeCompare(b.modelName))
          .map(model => ({
            ...model,
            vehicles: model.vehicles.sort((a, b) => (a.internalId || '').localeCompare(b.internalId || ''))
          }))
      }));
  }

  // Toggle model node expand/collapse
  toggleModelExpand(model: VehicleModelNode, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    model.expanded = !model.expanded;
    this.cdr.markForCheck();
  }

  // Select/deselect all vehicles in a model
  toggleModelSelection(model: VehicleModelNode, selectionMode: string, event?: any): void {
    if (selectionMode !== 'all') return;

    // Usa event.checked per determinare l'intenzione dell'utente
    const isSelecting = event?.checked ?? false;

    // Cap check SINCRONO: se siamo al cap e si tenta di selezionare, blocca subito
    if (isSelecting && this.selectedVehicleCustomers.length >= MAX_ASSETS_LOAD) {
      this.showMaxAssetsWarning();
      this.cdr.markForCheck();
      return;
    }

    const newVehicles = model.vehicles.filter(v => !this.selectedVehicleUidsSet.has(v.uid));

    // Usa setTimeout per non bloccare il thread
    setTimeout(() => {
      if (!isSelecting) {
        // DESELECT - rimuovi tutti i veicoli di questo model (sempre permesso, nessun warning)
        const modelVehicleUids = new Set(model.vehicles.map(v => v.uid));
        this.selectedVehicleCustomers = this.selectedVehicleCustomers.filter(v => !modelVehicleUids.has(v.uid));
        modelVehicleUids.forEach(uid => this.selectedVehicleUidsSet.delete(uid));
      } else {
        // SELECT - aggiungi i veicoli mancanti, controlla limite MAX_ASSETS_LOAD
        const totalAfterSelection = this.selectedVehicleCustomers.length + newVehicles.length;

        if (totalAfterSelection > MAX_ASSETS_LOAD) {
          const availableSlots = MAX_ASSETS_LOAD - this.selectedVehicleCustomers.length;
          if (availableSlots <= 0) {
            this.showMaxAssetsWarning();
            this.cdr.markForCheck();
            return;
          }

          // Seleziona solo quelli che ci stanno
          const vehiclesToAdd = newVehicles.slice(0, availableSlots);
          this.selectedVehicleCustomers = [...this.selectedVehicleCustomers, ...vehiclesToAdd];
          vehiclesToAdd.forEach(v => this.selectedVehicleUidsSet.add(v.uid));
          this.showMaxAssetsWarning();
        } else {
          this.selectedVehicleCustomers = [...this.selectedVehicleCustomers, ...newVehicles];
          newVehicles.forEach(v => this.selectedVehicleUidsSet.add(v.uid));
        }
      }

      this.updateSelectAllVehiclesLabel();
      this.onSelectionChangedVehicleCustomer();
      this.cdr.markForCheck();
    }, 0);
  }

  // Check if all vehicles in a model are selected - O(n) con Set lookup O(1)
  isModelFullySelected(model: VehicleModelNode): boolean {
    if (model.vehicles.length === 0) return false;
    for (const v of model.vehicles) {
      if (!this.selectedVehicleUidsSet.has(v.uid)) {
        return false;
      }
    }
    return true;
  }

  // Check if some (but not all) vehicles in a model are selected - O(n) con Set lookup O(1)
  isModelPartiallySelected(model: VehicleModelNode): boolean {
    let selectedCount = 0;
    for (const v of model.vehicles) {
      if (this.selectedVehicleUidsSet.has(v.uid)) {
        selectedCount++;
      }
    }
    return selectedCount > 0 && selectedCount < model.vehicles.length;
  }

  // Get customerName from vehicle's assignationHistory (latest entry by initDate)
  getVehicleCustomerName(vehicle: any): string {
    if (!vehicle?.assignationHistory || vehicle.assignationHistory.length === 0) {
      return '';
    }
    // Trova l'entry con initDate maggiore (che ha customerName aggiunto in processVehicles)
    const latestAssignation = vehicle.assignationHistory.reduce((latest: any, current: any) => {
      return (current.initDate > latest.initDate) ? current : latest;
    }, vehicle.assignationHistory[0]);
    return latestAssignation?.customerName || '';
  }

  // Get count of vehicles in a customer node
  getCustomerVehicleCount(customer: CustomerNode): number {
    return customer.models.reduce((sum, model) => sum + model.vehicles.length, 0);
  }

  // Toggle customer node expand/collapse
  toggleCustomerExpand(customer: CustomerNode): void {
    customer.expanded = !customer.expanded;
    this.cdr.markForCheck();
  }

  // Toggle model node expand/collapse in customer tree
  toggleCustomerModelExpand(model: VehicleModelNode, event: Event): void {
    event.stopPropagation();
    model.expanded = !model.expanded;
    this.cdr.markForCheck();
  }

  // Select/deselect all vehicles in a customer
  toggleCustomerSelection(customer: CustomerNode, selectionMode: string, event?: any): void {
    if (selectionMode !== 'all') return;

    // Usa event.checked per determinare l'intenzione dell'utente
    const isSelecting = event?.checked ?? false;

    // Cap check SINCRONO: se siamo al cap e si tenta di selezionare, blocca subito
    if (isSelecting && this.selectedVehicleCustomers.length >= MAX_ASSETS_LOAD) {
      this.showMaxAssetsWarning();
      this.cdr.markForCheck();
      return;
    }

    const customerVehicles: any[] = [];
    customer.models.forEach(model => {
      customerVehicles.push(...model.vehicles);
    });

    const newVehicles = customerVehicles.filter(v => !this.selectedVehicleUidsSet.has(v.uid));

    // Usa setTimeout per non bloccare il thread
    setTimeout(() => {
      if (!isSelecting) {
        // DESELECT - rimuovi tutti i veicoli di questo customer (sempre permesso, nessun warning)
        const customerVehicleUids = new Set(customerVehicles.map(v => v.uid));
        this.selectedVehicleCustomers = this.selectedVehicleCustomers.filter(v => !customerVehicleUids.has(v.uid));
        customerVehicleUids.forEach(uid => this.selectedVehicleUidsSet.delete(uid));
      } else {
        // SELECT - aggiungi i veicoli mancanti, controlla limite MAX_ASSETS_LOAD
        const totalAfterSelection = this.selectedVehicleCustomers.length + newVehicles.length;

        if (totalAfterSelection > MAX_ASSETS_LOAD) {
          const availableSlots = MAX_ASSETS_LOAD - this.selectedVehicleCustomers.length;
          if (availableSlots <= 0) {
            this.showMaxAssetsWarning();
            this.cdr.markForCheck();
            return;
          }

          // Seleziona solo quelli che ci stanno
          const vehiclesToAdd = newVehicles.slice(0, availableSlots);
          this.selectedVehicleCustomers = [...this.selectedVehicleCustomers, ...vehiclesToAdd];
          vehiclesToAdd.forEach(v => this.selectedVehicleUidsSet.add(v.uid));
          this.showMaxAssetsWarning();
        } else {
          this.selectedVehicleCustomers = [...this.selectedVehicleCustomers, ...newVehicles];
          newVehicles.forEach(v => this.selectedVehicleUidsSet.add(v.uid));
        }
      }

      this.updateSelectAllVehiclesLabel();
      this.onSelectionChangedVehicleCustomer();
      this.cdr.markForCheck();
    }, 0);
  }

  // Check if all vehicles in a customer are selected - O(n) con Set lookup O(1)
  isCustomerFullySelected(customer: CustomerNode): boolean {
    let hasVehicles = false;
    for (const model of customer.models) {
      for (const v of model.vehicles) {
        hasVehicles = true;
        if (!this.selectedVehicleUidsSet.has(v.uid)) {
          return false;
        }
      }
    }
    return hasVehicles;
  }

  // Check if some (but not all) vehicles in a customer are selected - O(n) con Set lookup O(1)
  isCustomerPartiallySelected(customer: CustomerNode): boolean {
    let selectedCount = 0;
    let totalCount = 0;
    for (const model of customer.models) {
      for (const v of model.vehicles) {
        totalCount++;
        if (this.selectedVehicleUidsSet.has(v.uid)) {
          selectedCount++;
        }
      }
    }
    return selectedCount > 0 && selectedCount < totalCount;
  }

  // TrackBy for customer nodes
  trackByCustomerUid(index: number, customer: CustomerNode): string {
    return customer.customerUid;
  }

  // TrackBy functions per ottimizzare rendering
  trackByVehicleUid(index: number, vehicle: any): string {
    return vehicle.uid;
  }

  trackByDriverUid(index: number, driver: any): string {
    return driver.uid;
  }

  trackByModelUid(index: number, model: VehicleModelNode): string {
    return model.modelUid;
  }

  /**
   * Mostra una label non bloccante quando viene raggiunto il limite MAX_ASSETS_LOAD
   */
  private showMaxAssetsWarning(): void {
    this.showMaxAssetsLabel = true;
    this.selectAllVehicles = true;
    this.textSelectAllVehicles = this.translateService.instant('global.deSelectAll');
    this.cdr.markForCheck();
  }

  /**
   * Sync selection with filtered vehicles - keep only selected vehicles that are still in filtered list
   */
  private syncSelectionWithFiltered(): void {
    const filteredUids = new Set(this.filteredVehicles.map(v => v.uid));
    this.selectedVehicleCustomers = this.selectedVehicleCustomers.filter(v => filteredUids.has(v.uid));
    this.syncVehicleSelectionSet();
    this.updateSelectAllVehiclesLabel();
    this.dispatchVehicleSelection();
  }

  /**
   * Toggle include children filter
   */
  toggleIncludeChildren(): void {
    this.includeChildren = !this.includeChildren;

    // Dispatch dei filtri nello store
    this.store.dispatch(PostActions.setVehicleFilters({
      includeChildren: this.includeChildren,
      alsoDisabled: this.alsoDisabled,
      alsoDataHidden: this.alsoDataHidden,
      subscribed: this.subscribed,
      haveCommunicated: this.haveCommunicated
    }));

    this.performVehicleFilter(this.searchTextVehicles);
    this.syncSelectionWithFiltered();
  }

  /**
   * Toggle also disabled filter
   */
  toggleAlsoDisabled(): void {
    this.alsoDisabled = !this.alsoDisabled;

    // Dispatch dei filtri nello store
    this.store.dispatch(PostActions.setVehicleFilters({
      includeChildren: this.includeChildren,
      alsoDisabled: this.alsoDisabled,
      alsoDataHidden: this.alsoDataHidden,
      subscribed: this.subscribed,
      haveCommunicated: this.haveCommunicated
    }));

    this.performVehicleFilter(this.searchTextVehicles);
    this.syncSelectionWithFiltered();
  }

  /**
   * Toggle subscribed filter
   */
  toggleSubscribed(): void {
    this.subscribed = !this.subscribed;

    // Dispatch dei filtri nello store
    this.store.dispatch(PostActions.setVehicleFilters({
      includeChildren: this.includeChildren,
      alsoDisabled: this.alsoDisabled,
      alsoDataHidden: this.alsoDataHidden,
      subscribed: this.subscribed,
      haveCommunicated: this.haveCommunicated
    }));

    this.performVehicleFilter(this.searchTextVehicles);
    this.syncSelectionWithFiltered();
  }

  /**
   * Toggle also data hidden filter
   */
  toggleAlsoDataHidden(): void {
    this.alsoDataHidden = !this.alsoDataHidden;

    // Dispatch dei filtri nello store
    this.store.dispatch(PostActions.setVehicleFilters({
      includeChildren: this.includeChildren,
      alsoDisabled: this.alsoDisabled,
      alsoDataHidden: this.alsoDataHidden,
      subscribed: this.subscribed,
      haveCommunicated: this.haveCommunicated
    }));

    this.performVehicleFilter(this.searchTextVehicles);
    this.syncSelectionWithFiltered();
  }

  /**
   * Toggle have communicated filter
   */
  toggleHaveCommunicated(): void {
    this.haveCommunicated = !this.haveCommunicated;

    // Dispatch dei filtri nello store
    this.store.dispatch(PostActions.setVehicleFilters({
      includeChildren: this.includeChildren,
      alsoDisabled: this.alsoDisabled,
      alsoDataHidden: this.alsoDataHidden,
      subscribed: this.subscribed,
      haveCommunicated: this.haveCommunicated
    }));

    this.performVehicleFilter(this.searchTextVehicles);
    this.syncSelectionWithFiltered();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.searchVehicles$.complete();
    this.searchDrivers$.complete();
  }
}
